import { isEqual } from 'lodash';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Prompt } from 'react-router';
import { toast } from 'react-toastify';
import { useDeepCompareEffectNoCheck } from 'use-deep-compare-effect';
import * as consts from '../../../consts/consts';
import { useFeatures } from '../../../utils/util/features';
import { renderValidationError } from '../../../utils/util/formatter';
import { useOrganizationSettings } from '../../../utils/util/hooks';
import { unloadWarning } from '../../../utils/util/util';
import ValidatedInput from '../Inputs/ValidatedInput';
import { INPUT_TYPES } from '../Inputs/ValidatedInputTypes';
import ConfirmForm from './ConfirmForm';
import { AutosaveParams, AutosaveResponse, useAutosave } from './hooks';
import { useValidatedForm } from './ValidatedFormContext';

const TIME_TO_WAIT_TO_AUTOSAVE_IN_MS = 1000;

const DEFAULT_OBJECT = {};

const DEFAULT_DRAFT_AUTOSAVE_EXTRA_FORM_KEYS_TO_CLEANUP_ON_SUBMIT = [];
const DEFAULT_MATCHING_VALIDATOR_NAMES = [];
const DEFAULT_RENDER_VALIDATION_ERRORS:
  | ((...args: any[]) => any)
  | null
  | undefined = (x) => x;
const DEFAULT_AUTOSAVE_SAVING_COMPONENT = (
  <span className="small">
    <i
      className="spinner-border"
      style={{
        width: '0.8rem',
        height: '0.8rem',
        position: 'relative',
        top: '-2px',
      }}
    />
    <span className="ms-2">
      <FormattedMessage
        id="app.views.widgets.forms.validated_form.saving"
        defaultMessage="Saving..."
      />
    </span>
  </span>
);
const DEFAULT_AUTOSAVE_SAVED_COMPONENT = (
  <span className="small">
    <i className="fe fe-check me-2" />
    <FormattedMessage
      id="app.views.widgets.forms.validated_form.saved"
      defaultMessage="Saved"
    />
  </span>
);
const DEFAULT_AUTOSAVE_ERROR_COMPONENT = (
  <span className="small text-danger">
    <i
      className="fe fe-x-circle me-2"
      style={{ position: 'relative', top: '1px' }}
    />
    <FormattedMessage
      id="app.views.widgets.forms.validated_form.error"
      defaultMessage="Error"
    />
  </span>
);
const DEFAULT_ON_VALIDATE = () => ({});

export type OnInputChangeEventsTransformer = (
  event: any[] | any,
  object: any
) => any[] | any;

export type OnInputChange = (
  e: any,
  onChangeSideEffects?: any,
  eventsTransformer?: OnInputChangeEventsTransformer
) => void | undefined;

export interface ValidatedFormProps {
  formRef?: React.RefObject<any>;
  title?: string;
  ariaLabel?: string;
  role?: string;
  autoFocus?: boolean | null;
  focusRef?: React.RefObject<any>;
  callback: (data?: any, error?: any, hardErrorMessage?: string | null) => void;
  renderInputs?: ((inputElements: React.ReactNode[]) => React.ReactNode) | null;
  renderForm?:
    | ((children: any, button: any, draftWarning: any) => React.ReactNode)
    | null;
  renderValidationErrors?: (errors: any) => React.ReactNode;
  object?: Record<string, any> | null;
  inputs?: (object | null | undefined)[] | null;
  submitText?: string | React.ReactNode;
  className?: string | null;
  buttonClassName?: string | null;
  buttonIsBlock?: boolean;
  buttonType?: string | null;
  url?: string | null;
  action?: string | null;
  method?: string | null;
  submitPromise?: ((data: any) => Promise<any>) | null;
  onValidate?: ((obj: any) => Record<string, any>) | null;
  preSubmit?: (() => void) | null;
  matchingValidatorNames?: string[];
  transformObjectBeforeSubmit?: ((obj: any) => any) | null;
  children?: React.ReactNode | React.ReactNode[];
  hasUnsavedChanges?: boolean | null;
  setHasUnsavedChanges?: (hasUnsavedChanges: boolean) => void;
  setShouldShowPrompt?: (shouldShowPrompt: boolean) => void;
  anchorTrigger?: string | null;
  style?: React.CSSProperties | null;
  inlineSubmitButton?: boolean | null;
  inForm?: boolean | null;
  onChange?: ((newObject: any) => void) | null;
  onReady?: (refs: {
    onInputChangeRef: React.MutableRefObject<OnInputChange | undefined>;
    objectRef: React.MutableRefObject<unknown>;
  }) => void;
  hideSubmitButton?: boolean | null;
  translationNamespace?: string | null;
  translationVisibility?: string | null;
  submitOnChange?: boolean;
  disableUnsavedChangesPrompt?: boolean | null;
  doNotSendOrganization?: boolean;
  organization?: Record<string, any>;
  setAutosaveStatus?: ((status: React.ReactNode) => void) | null;
  draftAutosaveEnabled?: boolean;
  autosaveSavingComponent?: React.ReactNode;
  autosaveSavedComponent?: React.ReactNode;
  autosaveErrorComponent?: React.ReactNode;
  belowFormContents?: React.ReactNode;
  uniqueFormKey?: string;
  draftAutosaveExtraFormKeysToCleanupOnSubmit?: string[];
  confirmationDialog?: Record<string, any>;
  readOnly?: boolean | null;
  disabled?: boolean | null;
  disabledHoverText?: string;
  isSubmitting?: boolean | null;
  noValidate?: boolean | null;
  buttonColor?: string;
  jsonPath?: string | null;
  onInputsChange?: ((object: any, clear: () => void) => void) | null;
}

const ValidatedForm: FC<ValidatedFormProps> = ({
  autoFocus = true,
  object: propsObject = DEFAULT_OBJECT,
  draftAutosaveEnabled: propsDraftAutosaveEnabled = false,
  draftAutosaveExtraFormKeysToCleanupOnSubmit:
    propsDraftAutosaveExtraFormKeysToCleanupOnSubmit = DEFAULT_DRAFT_AUTOSAVE_EXTRA_FORM_KEYS_TO_CLEANUP_ON_SUBMIT,
  matchingValidatorNames:
    propsMatchingValidatorNames = DEFAULT_MATCHING_VALIDATOR_NAMES,
  renderValidationErrors:
    propsRenderValidationErrors = DEFAULT_RENDER_VALIDATION_ERRORS,
  autosaveSavingComponent = DEFAULT_AUTOSAVE_SAVING_COMPONENT,
  autosaveSavedComponent = DEFAULT_AUTOSAVE_SAVED_COMPONENT,
  autosaveErrorComponent = DEFAULT_AUTOSAVE_ERROR_COMPONENT,
  onValidate: propsOnValidate = DEFAULT_ON_VALIDATE,
  onReady,
  ...props
}) => {
  // Feature Toggled Autosave
  const { features } = useFeatures();
  const intl = useIntl();
  const validatedForm = useValidatedForm();
  const organizationSettings = useOrganizationSettings();
  const [object, setObject] = useState(propsObject);
  const [inputsValidationErrors, setInputValidationErrors] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [objectHasUnsavedChanges, setObjectHasUnsavedChanges] = useState(false);
  const [triggerCallbackData, setTriggerCallbackData] = useState(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isFirstRun = useRef(true);
  const propsCallback = props.callback;
  const propsSetHasUnsavedChanges = useMemo(
    () => props.setHasUnsavedChanges,
    [props.setHasUnsavedChanges]
  );
  const propsOnChange = props.onChange;
  const propsSubmitOnChange = props.submitOnChange;
  const internalOnlyRef = useRef();
  const formRef = useMemo(
    () => (props.formRef ? props.formRef : internalOnlyRef),
    [props.formRef]
  );

  const lastSavedObject = useRef(propsObject);

  const autosaveTimeoutId = useRef(null);

  // autosave is for live forms that should periodically save
  const propsSetAutosaveStatus = props.setAutosaveStatus;

  // partial-autosave is for forms that should save immediately but not submit
  const draftAutosaveEnabled =
    propsDraftAutosaveEnabled && (features?.autosave_forms?.enabled ?? true);

  // Disable legacy unsaved changes prompt if
  const propsDisableUnsavedChangesPrompt =
    props.disableUnsavedChangesPrompt || draftAutosaveEnabled;

  if (draftAutosaveEnabled && !props.uniqueFormKey) {
    throw new Error(
      'uniqueFormKey identifier must be provided if you want to enable the automatic unsaved changes functionality'
    );
  }

  const {
    stash: saveDraft,
    discard: discardDraft,
    state: draftFetchState,
    latestObjectFetched: latestDraftObjectFetched,
    deleteRemote: deleteDraftFromServer,
    hasPendingChanges: draftHasPendingChangesToSync,
    isChangedFromSubmitted: isInDraftState,
  }: AutosaveResponse = useAutosave({
    key: props.uniqueFormKey ?? '',
    enabled: draftAutosaveEnabled,
    inputs: props.inputs,
    submittedObject: propsObject,
    extraKeysToCleanupOnDeleteRemote:
      propsDraftAutosaveExtraFormKeysToCleanupOnSubmit,
  } as AutosaveParams);

  // Using a ref to avoid re-creating the function on every render
  const onInputChangeRef = useRef<OnInputChange>();

  const objectRef = useRef<unknown>(object);
  objectRef.current = object;

  useEffect(() => {
    if (onReady) {
      // Add to onReady everything that needs to be exposed to the parent component
      onReady({ onInputChangeRef, objectRef });
    }
  }, [onReady]);

  useEffect(() => {
    if (draftFetchState === 'SUCCESS') {
      // @ts-expect-error
      setObject(latestDraftObjectFetched);
    }
    if (draftFetchState === 'ERROR') {
      toast.error(
        intl.formatMessage({
          id: 'app.views.widgets.forms.validated_form.autosave.loading.error',
          defaultMessage:
            'Error loading previously draft data. Please refresh the page to load the latest. If the problem persists, please contact support.',
        })
      );
    }
  }, [draftFetchState, latestDraftObjectFetched, intl]);

  // update object if new one passed in
  useDeepCompareEffectNoCheck(() => {
    // NOTE: we DON'T call propsSetHasUnsavedChanges(false) here because
    // we want to keep the unsaved changes state if the user has unsaved changes
    // but this useEffect will get called if propsSetHasUnsavedChanges is
    // a dependency
    setObjectHasUnsavedChanges(false);
    setObject(propsObject);
  }, [propsObject]);

  // we allow passing in an override hasUnsavedChanges externally for when multiple
  // forms or fields are working together
  const hasUnsavedChanges = useMemo(() => {
    if (propsDisableUnsavedChangesPrompt) {
      return false;
    }

    return typeof props.hasUnsavedChanges !== 'undefined'
      ? props.hasUnsavedChanges
      : objectHasUnsavedChanges;
  }, [
    objectHasUnsavedChanges,
    propsDisableUnsavedChangesPrompt,
    props.hasUnsavedChanges,
  ]);

  const shouldShowPrompt =
    (!propsDisableUnsavedChangesPrompt &&
      hasUnsavedChanges &&
      !draftAutosaveEnabled) ||
    draftHasPendingChangesToSync;

  const propsSetShouldShowPrompt = props.setShouldShowPrompt;
  useEffect(() => {
    propsSetShouldShowPrompt?.(shouldShowPrompt);
  }, [propsSetShouldShowPrompt, shouldShowPrompt]);

  // show prompt if clicking back button or navigating away via refresh
  // (note: <Prompt /> below doesn't cover refresh but this does)
  // TODO: figure out why sometimes in some situations clicking back
  // still removes data from the page, i.e. doesn't fully cancel it
  useEffect(() => unloadWarning(shouldShowPrompt), [shouldShowPrompt]);

  // if object changed and props.submitOnChange is set, call it
  // NOTE: we check first run to avoid calling the function on initialization
  useEffect(() => {
    if (propsSubmitOnChange) {
      if (!isEqual(lastSavedObject.current, object)) {
        lastSavedObject.current = object;
        formRef.current?.props.onSubmit();
        setObjectHasUnsavedChanges(false);
        if (propsSetHasUnsavedChanges) {
          propsSetHasUnsavedChanges(false);
        }
      }
    } else if (isFirstRun.current) {
      isFirstRun.current = false;
    } else if (propsSetAutosaveStatus) {
      // only resave if it has changed
      if (hasUnsavedChanges && !isEqual(lastSavedObject.current, object)) {
        // set short timer (so we don't save EVERY keystroke)
        // and when reached, try auto-saving
        if (autosaveTimeoutId?.current) {
          clearTimeout(autosaveTimeoutId.current);
        }

        propsSetAutosaveStatus(autosaveSavingComponent);
        // @ts-expect-error
        autosaveTimeoutId.current = setTimeout(() => {
          lastSavedObject.current = object;
          return formRef.current?.props.onSubmit();
        }, TIME_TO_WAIT_TO_AUTOSAVE_IN_MS);
      }
    } else {
      // remove any existing autosave timeout (this is
      // for the case when we disable the autosave feature,
      // e.g. temporarily when showing prompt to delete goal)
      if (autosaveTimeoutId?.current) {
        clearTimeout(autosaveTimeoutId.current);
      }
    }
  }, [
    object,
    propsSubmitOnChange,
    propsSetAutosaveStatus,
    hasUnsavedChanges,
    propsObject,
    formRef,
    propsSetHasUnsavedChanges,
    autosaveSavingComponent,
    autosaveSavedComponent,
  ]);

  const onInputValidChange = useCallback(
    (name, validationMessage) => {
      if (validationMessage) {
        const oldMessage = inputsValidationErrors[name];
        if (oldMessage !== validationMessage) {
          setInputValidationErrors({
            ...inputsValidationErrors,
            [name]: validationMessage,
          });
        }
      } else {
        if (inputsValidationErrors[name]) {
          const errors = { ...inputsValidationErrors };
          delete errors[name];
          setInputValidationErrors(errors);
        }
      }
    },
    [inputsValidationErrors]
  );

  const onSubmitCallback = useCallback(
    (data, error, hardErrorMessage = null) => {
      setValidationErrors({});

      if (error || hardErrorMessage) {
        if (propsSetAutosaveStatus) {
          propsSetAutosaveStatus(autosaveErrorComponent);
        }

        // show validation errors if error is set
        if (error?.data) {
          setValidationErrors(error.data);
        }

        // note: only error or HardErrorMessage should be set
        propsCallback(null, error, hardErrorMessage);

        // manually set isSubmitting to false here after
        // the callback executes to avoid flickering of the submit button
        setIsSubmitting(false);
      } else {
        if (propsSetAutosaveStatus) {
          // only show a saved indicator if the save was successful
          propsSetAutosaveStatus(autosaveSavedComponent);
        }

        // ensure we don't see "You have unsaved changes" dialog anymore
        // since save was successful
        setObjectHasUnsavedChanges(false);
        if (propsSetHasUnsavedChanges) {
          propsSetHasUnsavedChanges(false);
        }

        if (draftAutosaveEnabled) {
          // delete draft from server and only then proceed with callback
          // to avoid having concurrency issues where the draft gets loaded
          // before the draft is deleted
          deleteDraftFromServer(() => setTriggerCallbackData(data));
        } else {
          setTriggerCallbackData(data);
        }
      }
    },
    [
      propsSetAutosaveStatus,
      propsCallback,
      autosaveErrorComponent,
      autosaveSavedComponent,
      propsSetHasUnsavedChanges,
      draftAutosaveEnabled,
      deleteDraftFromServer,
    ]
  );

  useEffect(() => {
    // we trigger this in a callback to ensure that setHasUnsavedChanges
    // is set to true before calling the callback, else we may inadvertently
    // show the "You have unsaved changes" dialog after the person clicks submit
    // if it takes the user to a new page
    if (typeof triggerCallbackData !== 'undefined') {
      // ensure we don't see "You have unsaved changes" dialog anymore
      // since save was successful
      setObjectHasUnsavedChanges(false);
      if (propsSetHasUnsavedChanges) {
        propsSetHasUnsavedChanges(false);
      }

      const callbackDataValue = triggerCallbackData;

      // don't call callback more than once
      setTriggerCallbackData(undefined);

      // success; return success data
      propsCallback(callbackDataValue);

      // manually set isSubmitting to false here after
      // the callback executes to avoid flickering of the submit button
      setIsSubmitting(false);
    }
  }, [
    hasUnsavedChanges,
    propsCallback,
    propsSetHasUnsavedChanges,
    triggerCallbackData,
  ]);

  const clear = useCallback(() => {
    setValidationErrors({});
    setInputValidationErrors({});
  }, []);

  const propsOnInputsChange = props.onInputsChange;
  useEffect(() => {
    if (propsOnInputsChange) {
      propsOnInputsChange(object, clear);
    }
  }, [props.inputs, object, clear, propsOnInputsChange]);

  const onInputChange: OnInputChange = useCallback(
    (e, onChangeSideEffects, eventsTransformer) => {
      // for autosaving properly (and not double-autosaving when, say,
      // creating something and then passing back the created object to
      // this component as a prop right after)
      setObjectHasUnsavedChanges(true);
      if (propsSetHasUnsavedChanges) {
        propsSetHasUnsavedChanges(true);
      }

      if (eventsTransformer && e) {
        e = eventsTransformer(e, object);
      }

      // Additional side effects in case we need to update other fields
      const sideEffects = onChangeSideEffects
        ? onChangeSideEffects(
            object,
            e.target.value,
            intl,
            organizationSettings,
            e.target.name
          )
        : {};

      const newObject =
        // Case when forced multiple inputs to change at once
        e instanceof Array
          ? {
              ...object,
              ...e.reduce(
                (acc, el) => ({ ...acc, [el.target.name]: el.target.value }),
                {}
              ),
              ...sideEffects,
            }
          : // Case when single input change
            {
              ...object,
              [e.target.name]: e.target.value,
              ...sideEffects,
            };
      setObject(newObject);
      if (propsOnChange) {
        propsOnChange(newObject);
      }
      if (draftAutosaveEnabled) {
        saveDraft(newObject);
      }
    },
    [
      propsSetHasUnsavedChanges,
      object,
      propsOnChange,
      draftAutosaveEnabled,
      saveDraft,
      intl,
      organizationSettings,
    ]
  );

  useEffect(() => {
    onInputChangeRef.current = onInputChange;
  }, [onInputChange]);

  // returns false if error, true if all is good
  const onValidate = useCallback(
    (obj) => {
      const filteredInputValidationErrors = props.inputs?.reduce(
        (acc, input) => {
          // @ts-expect-error
          const e = inputsValidationErrors[input.name];
          if (e) {
            return {
              ...acc,
              // @ts-expect-error
              [input.name]: inputsValidationErrors[input.name],
            };
          }
          return acc;
        },
        {}
      );
      const errors = {
        ...filteredInputValidationErrors,
        // custom validation messages will take precedence
        ...(propsOnValidate ? propsOnValidate(obj) : {}),
      };

      // only get errors that exist
      for (const i in errors) {
        if (errors[i]) {
          setValidationErrors(errors);
          return false;
        }
      }

      return true;
    },
    [propsOnValidate, inputsValidationErrors, props.inputs]
  );

  const onIsSubmittingChange = (submitting) => {
    // we set to true only when submitting; we
    // set to not submitting only when the callback
    // is called to avoid flickering of the submit button
    // in the case of the callback in a delayed manner
    // via useEffect (e.g. when deleteDraftFromServer used)
    if (submitting) {
      setIsSubmitting(true);
    }
  };

  const anInputHasManualAutofocus =
    // @ts-expect-error
    props.inputs && props.inputs.findIndex((i) => i.autoFocus === true) !== -1;

  // if any validation errors don't have a corresponding input,
  // show them at the bottom
  const matchingValidatorNames =
    props.inputs && propsMatchingValidatorNames
      ? // @ts-expect-error
        propsMatchingValidatorNames.concat(props.inputs.map((i) => i.name))
      : [];

  const errorsAreArray = Array.isArray(validationErrors);
  const unattachedValidationErrors = errorsAreArray
    ? validationErrors
    : Object.keys(validationErrors)
        .filter((name) => matchingValidatorNames.indexOf(name) === -1)
        .reduce((acc, name) => {
          acc[name] = validationErrors[name];
          return acc;
        }, {});
  const attachedValidationErrors = errorsAreArray
    ? {}
    : Object.keys(validationErrors)
        .filter((name) => matchingValidatorNames.indexOf(name) !== -1)
        .reduce((acc, name) => {
          acc[name] = validationErrors[name];
          return acc;
        }, {});
  const hasUnattachedErrors =
    Object.keys(unattachedValidationErrors).length > 0;
  const hasAttachedErrors = Object.keys(attachedValidationErrors).length > 0;

  let children = props.children;

  // if isSubmitting is provided externally, defer to that instead
  // of dictating it ourselves for controlling button enabling; note that if triggerCallbackData
  // is still defined, that means we haven't yet called the callback, so we should still
  // consider it as submitting
  const finalIsSubmitting = useMemo(() => {
    return (props.isSubmitting ??
      // only override the behavior if populated, else set to undefined
      // so ConfirmForm decides what to do independently
      (isSubmitting || typeof triggerCallbackData !== 'undefined'))
      ? true
      : undefined;
  }, [isSubmitting, props.isSubmitting, triggerCallbackData]);

  const inputWrapper = validatedForm?.inputWrapper;
  const outputWrapper = validatedForm?.outputWrapper;

  const inputElements = useMemo(
    () =>
      (props.inputs ?? []).map((rawinput, index) => {
        const input: unknown = inputWrapper
          ? inputWrapper(rawinput, object)
          : rawinput;
        // @ts-expect-error
        const value = input?.name && object && object[input.name];

        const output = (
          // @ts-expect-error
          <ValidatedInput
            disableUnsavedChangesPrompt={propsDisableUnsavedChangesPrompt}
            key={index}
            // @ts-expect-error
            innerRef={input.innerRef}
            // @ts-expect-error
            {...input}
            disabled={
              // @ts-expect-error
              props.readOnly || input.disabled || draftFetchState === 'LOADING'
            }
            value={
              // @ts-expect-error
              input?.type === INPUT_TYPES.GROUP
                ? Object.fromEntries(
                    // @ts-expect-error
                    input.group.map((it) => [it.name, object?.[it.name]])
                  )
                : value
            }
            // @ts-expect-error
            onChange={(e) => onInputChange(e, input.onChangeSideEffects)}
            onValidChange={onInputValidChange}
            validationErrors={errorsAreArray ? undefined : validationErrors}
            // enable autofocus if specified manually for cases where form
            // is hidden manually somehow and later shown (and not explicitly set to false)
            autoFocus={
              (!anInputHasManualAutofocus &&
                autoFocus &&
                index === 0 &&
                // @ts-expect-error
                input.autoFocus !== false) ||
              // @ts-expect-error
              input.autoFocus
            }
            isSubmitting={finalIsSubmitting}
            organization={props.organization}
            translationNamespace={
              // @ts-expect-error
              props.translationNamespace || input.translationNamespace
            }
            translationVisibility={props.translationVisibility}
            // @ts-expect-error
            jsonPath={input.jsonPath}
          />
        );

        return outputWrapper?.(output) ?? output;
      }),
    [
      props.inputs,
      props.readOnly,
      autoFocus,
      props.organization,
      props.translationNamespace,
      props.translationVisibility,
      object,
      propsDisableUnsavedChangesPrompt,
      draftFetchState,
      onInputValidChange,
      errorsAreArray,
      validationErrors,
      anInputHasManualAutofocus,
      finalIsSubmitting,
      onInputChange,
      inputWrapper,
      outputWrapper,
    ]
  );

  // if no children provided automatically, render children based on fields passed into object,
  // and set focus into first child when created
  if (!children && props.inputs) {
    children = props.renderInputs ? (
      props.renderInputs(inputElements)
    ) : (
      <>{inputElements}</>
    );
  }

  // @ts-expect-error
  const formContents = children && children.length > 1 ? children[0] : children;
  const belowFormContents = useMemo(
    () =>
      props.belowFormContents ||
      // @ts-expect-error
      (children && children.length >= 2 ? children[1] : ''),
    [children, props.belowFormContents]
  );

  const renderedUnattachedErrors =
    hasUnattachedErrors &&
    propsRenderValidationErrors(
      renderValidationError(unattachedValidationErrors)
    );

  // submitOnChange means we don't have a submit button, we're just changing the objects
  // as they change (e.g. a notification form), so we should show errors as toasts instead
  // of on the form itself. Similarly, setAutosaveStatus means we're autosaving (but with
  // a time delay, e.g. for a text form where we only want to save after a few seconds of
  // inactivity).
  const showErrorsAsToasts =
    props.submitOnChange || props.setAutosaveStatus ? true : false;
  const leavePromptMessage = consts.UNSAVED_CHANGES_PROMPT;

  const propsPreSubmit = props.preSubmit;
  const preSubmit = useCallback(() => {
    if (propsPreSubmit) {
      propsPreSubmit();
    }
  }, [propsPreSubmit]);

  const prompt = useMemo(
    () => <Prompt when={shouldShowPrompt} message={leavePromptMessage} />,
    [shouldShowPrompt, leavePromptMessage]
  );

  const output = useMemo(
    () =>
      props.inForm ? (
        <>
          <div>
            {formContents}
            {renderedUnattachedErrors}
          </div>
          {belowFormContents}
        </>
      ) : (
        <>
          {prompt}
          <ConfirmForm
            url={props.url}
            action={props.action}
            method={props.method}
            submitPromise={props.submitPromise}
            formRef={formRef}
            object={object}
            submitText={props.submitText ? props.submitText : props.title}
            inlineSubmitButton={props.inlineSubmitButton}
            renderForm={props.renderForm}
            renderValidationErrors={propsRenderValidationErrors}
            className={props.className}
            buttonClassName={props.buttonClassName}
            buttonColor={props.buttonColor}
            buttonIsBlock={props.buttonIsBlock}
            buttonType={props.buttonType}
            onValidate={onValidate}
            preSubmit={preSubmit}
            transformObjectBeforeSubmit={props.transformObjectBeforeSubmit}
            callback={onSubmitCallback}
            doNotSendOrganization={props.doNotSendOrganization}
            showErrorsAsToasts={showErrorsAsToasts}
            hideSubmitButton={props.hideSubmitButton || props.submitOnChange}
            confirmationDialog={props.confirmationDialog}
            disabled={props.disabled}
            disabledHoverText={props.disabledHoverText}
            onIsSubmittingChange={onIsSubmittingChange}
            isSubmitting={finalIsSubmitting}
            noValidate={props.noValidate}
            isInDraftState={isInDraftState}
            discardDraft={discardDraft}
            draftFetchState={draftFetchState}
            autoFocusSubmitButton={!anInputHasManualAutofocus}
            ariaLabel={props.ariaLabel}
            role={props.role}
          >
            {formContents}
          </ConfirmForm>
          {!showErrorsAsToasts ? renderedUnattachedErrors : undefined}
          {!showErrorsAsToasts && hasAttachedErrors && !hasUnattachedErrors && (
            <div className="text-center pt-2">
              <span className="text-danger">
                <FormattedMessage
                  id="app.views.widgets.forms.validated_form.inline_errors_hint"
                  defaultMessage="Please review and fix the errors highlighted above."
                />
              </span>
            </div>
          )}
          {belowFormContents}
        </>
      ),
    [
      props.inForm,
      props.url,
      props.action,
      props.method,
      props.submitPromise,
      props.submitText,
      props.title,
      props.inlineSubmitButton,
      props.renderForm,
      propsRenderValidationErrors,
      props.className,
      props.buttonClassName,
      props.buttonColor,
      props.buttonIsBlock,
      props.buttonType,
      props.transformObjectBeforeSubmit,
      props.doNotSendOrganization,
      props.hideSubmitButton,
      props.submitOnChange,
      props.confirmationDialog,
      props.disabled,
      props.disabledHoverText,
      props.noValidate,
      props.ariaLabel,
      props.role,
      formContents,
      renderedUnattachedErrors,
      hasAttachedErrors,
      hasUnattachedErrors,
      belowFormContents,
      prompt,
      formRef,
      object,
      onValidate,
      preSubmit,
      onSubmitCallback,
      showErrorsAsToasts,
      finalIsSubmitting,
      isInDraftState,
      discardDraft,
      draftFetchState,
      anInputHasManualAutofocus,
    ]
  );

  return output;
};

export default React.memo(ValidatedForm);
